import { queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import type { QuoteBuilderApi } from '../api/client'
import type { Customer, NewCustomer } from '../api/types'

// Communications (USA-1032), ported from the demo's features/communications/api.ts. Threads are
// the signed-in user's own inbox; the mailbox read and refresh are the port's additions.

export type Communication = components['schemas']['QbCommunicationRead']
export type CommunicationAttachment = components['schemas']['QbCommunicationAttachment']
export type Mailbox = components['schemas']['QbCommunicationMailbox']
export type InboxFilter = 'all' | 'unread' | 'unquoted' | 'in_progress' | 'needs_attention' | 'complete'
export type CommunicationProgress = Pick<Communication, 'id' | 'status' | 'progress' | 'error'> & { steps: string[] }

export const communicationsKey = ['communications'] as const
export const mailboxKey = ['communications', 'mailbox'] as const
export const isCreating = (thread: Communication) => thread.status === 'queued' || thread.status === 'running'
// No customer is needed: the quote is built from what the emails say (USA-1140).
export const canCreate = (thread: Communication) => thread.status === 'idle' || thread.status === 'failed'

export function matchesThread(thread: Communication, search: string, filter: InboxFilter) {
  const matchesFilter =
    filter === 'all' ||
    (filter === 'unread' && !thread.is_read) ||
    (filter === 'unquoted' && thread.status === 'idle') ||
    (filter === 'in_progress' && isCreating(thread)) ||
    (filter === 'needs_attention' && (thread.status === 'failed' || thread.status === 'needs_review')) ||
    (filter === 'complete' && thread.status === 'complete')
  const text = [
    thread.subject,
    thread.sender_name,
    thread.sender_email,
    thread.customer_name,
    ...thread.messages.map((message) => message.body),
    ...thread.attachments.map((file) => file.name),
    thread.quote?.quote_number,
    thread.quote?.title,
  ]
    .join(' ')
    .toLocaleLowerCase()
  return matchesFilter && search.trim().toLocaleLowerCase().split(/\s+/).every((word) => text.includes(word))
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

export const communicationsApi = (api: QuoteBuilderApi) => ({
  list: (signal?: AbortSignal) => api.request<Communication[]>('/communications', { signal }),
  mailbox: (signal?: AbortSignal) => api.request<Mailbox>('/communications/mailbox', { signal }),
  refresh: () => api.request<Mailbox>('/communications/refresh', { method: 'POST' }),
  markRead: (threadIds: string[], isRead: boolean) =>
    api.request<Communication[]>('/communications/read', json({ thread_ids: threadIds, is_read: isRead })),
  createQuotes: (threadIds: string[]) =>
    api.request<Communication[]>('/communications/quotes', json({ thread_ids: threadIds })),
  chooseCustomer: (threadId: string, customerId: number) =>
    api.request<Communication>(`/communications/${threadId}/customer`, {
      method: 'PUT',
      body: JSON.stringify({ customer_id: customerId }),
    }),
  createCustomer: (customer: NewCustomer) => api.request<Customer>('/customers', json(customer)),
  attachment: async (threadId: string, attachmentId: string) => {
    const response = await api.fetch(`/communications/${threadId}/attachments/${encodeURIComponent(attachmentId)}`)
    if (!response.ok) throw new Error('Could not open this attachment.')
    return response.blob()
  },
  stream: async (onProgress: (updates: CommunicationProgress[]) => void, signal: AbortSignal) => {
    const response = await api.fetch('/communications/events', { signal, headers: { Accept: 'text/event-stream' } })
    if (!response.ok || !response.body) throw new Error('Live progress disconnected')
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffered = ''
    try {
      while (!signal.aborted) {
        const { value, done } = await reader.read()
        if (done) return
        buffered += decoder.decode(value, { stream: true })
        let boundary: number
        while ((boundary = buffered.indexOf('\n\n')) !== -1) {
          const event = buffered.slice(0, boundary)
          buffered = buffered.slice(boundary + 2)
          if (event.startsWith('data: ')) onProgress(JSON.parse(event.slice(6)) as CommunicationProgress[])
        }
      }
    } finally {
      await reader.cancel()
    }
  },
})

export const communicationsQueryOptions = (api: QuoteBuilderApi) =>
  queryOptions({
    queryKey: communicationsKey,
    queryFn: ({ signal }) => communicationsApi(api).list(signal),
    staleTime: 0,
    // New mail lands through the background inbox read; keep the list current while it is open.
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

export const mailboxQueryOptions = (api: QuoteBuilderApi) =>
  queryOptions({
    queryKey: mailboxKey,
    queryFn: ({ signal }) => communicationsApi(api).mailbox(signal),
    refetchInterval: 15_000,
  })
